import type { AxiosResponse, AxiosError } from 'axios';

export type Condition = () => boolean;

export type OnError = (error: AxiosError) => Promise<AxiosResponse>;

export type OnResponse = (response: AxiosResponse) => Promise<AxiosResponse>;

type InterceptorId = number | undefined;

/**
 * Custom composable for intercepting axios responses.
 *
 * This hook creates and cleans up route interceptors automatically based on the provided condition.
 * When the condition becomes true, the interceptor is added. When false, it's removed.
 *
 * @param condition - A reactive value that determines if the route interceptor should be active
 * @param onResponse - A callback function that executes when route changes while the condition is true
 * @param onError - A callback function that executes when route changes while the condition is true
 */
export const useAxiosResponseInterceptor = (condition: Condition, onResponse: OnResponse, onError: OnError) => {
  const { $axios } = useNuxtApp();

  let interceptorId: InterceptorId;

  watchImmediate(condition, (to) => {
    if (interceptorId !== undefined) {
      $axios.interceptors.response.eject(interceptorId);
    }

    if (to) {
      interceptorId = $axios.interceptors.response.use(onResponse, onError);
    }
  });
};
